/* ============================================================
   Trasse Families — Settings.

   Everything a household changes about itself, and — just as deliberately —
   the things it cannot change here, said out loud rather than left as a field
   that quietly breaks the account.

   Two of those are worth reading before editing this file:

   1. THE NUMBER PLATE IS THE VEHICLE'S ID. `busId === plate` (see
      families/signup.js buildActivationWrites). Every trip, route, refuel and
      child stop hangs off that id, so a plate "edit" is a migration, not a
      settings change. The field is shown read-only and says why.

   2. HOME IS TWO DOCUMENTS. The org holds `homeLocations`; every child on the
      bus holds a `stopLocation`. The geofence that ENDS the evening run reads
      the child's stop, so writing only the org would move the pin on screen
      and leave the run finishing at the old house. Both are written together,
      both sealed — `organizations` is readable by anyone holding the code, and
      a household's address is not a school's public address.
   ============================================================ */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection, doc, getDoc, getDocs, onSnapshot, query, serverTimestamp, setDoc, where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import LocationPicker from '../LocationPicker';
import { sendFleetInvite } from '../FleetJoinPage';
import { ensureLocationDek, openLatLng, sealLatLng, LatLng } from '../../utils/locationCrypto';
import { fetchMyFamilyReferralCode, inviteFamilyDriver, removeFamilyDriver } from '../../services/paymentsApi';
import FamilySchoolLinkPanel from './FamilySchoolLinkPanel';
import { FAM, DISPLAY_FONT, FAMILY_PAGE_WRAP } from './familyTheme';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

interface Props {
  orgCode: string;
  orgName: string;
}

interface DriverInfo {
  uid: string;
  name: string;
  email: string;
  addedAt: Date | null;
}

interface CoParent {
  uid: string;
  name: string;
  email: string;
}

interface PendingInvite {
  id: string;
  email: string;
}

function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') return (v as { toDate: () => Date }).toDate();
  if (v instanceof Date) return v;
  return null;
}

function fmtDay(d: Date | null): string {
  if (!d) return '';
  return d.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
}

/* ------------------------------------------------------------- pieces -- */
const Panel: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <section style={{
    background: '#fff', border: `1px solid ${FAM.line}`, borderRadius: 20,
    padding: 'clamp(18px,3vw,26px)', ...style,
  }}>
    {children}
  </section>
);

const H: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 style={{ fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: 30, letterSpacing: '-.01em', margin: 0 }}>
    {children}
  </h2>
);

const Sub: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.6, color: FAM.ink2, maxWidth: 580 }}>{children}</p>
);

const labelStyle: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: FAM.ink3,
};

const inputStyle: React.CSSProperties = {
  font: 'inherit', fontSize: 14, padding: '9px 11px', borderRadius: 10, width: '100%',
  border: `1px solid ${FAM.line}`, background: '#fff', color: FAM.ink, minWidth: 0,
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
    <span style={labelStyle}>{label}</span>
    {children}
  </label>
);

const primaryBtn = (busy: boolean): React.CSSProperties => ({
  padding: '10px 18px', borderRadius: 10, border: 'none',
  background: busy ? FAM.accentPale : FAM.accent,
  color: '#fff', font: 'inherit', fontSize: 14, fontWeight: 700,
  cursor: busy ? 'not-allowed' : 'pointer',
  boxShadow: busy ? 'none' : '0 4px 13px rgba(0,112,127,.4)',
});

const quietBtn: React.CSSProperties = {
  padding: '9px 15px', borderRadius: 10, border: `1px solid ${FAM.line}`,
  background: '#fff', color: FAM.ink2, font: 'inherit', fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
};

const Note: React.FC<{ tone: 'ok' | 'bad' | 'warn'; children: React.ReactNode }> = ({ tone, children }) => {
  const map = {
    ok: { bg: FAM.posTint, fg: FAM.pos },
    bad: { bg: FAM.dangerTint, fg: FAM.danger },
    warn: { bg: FAM.amberTint, fg: FAM.amber },
  }[tone];
  return (
    <p style={{
      margin: '12px 0 0', padding: '10px 13px', borderRadius: 10,
      background: map.bg, color: map.fg, fontSize: 13.5, lineHeight: 1.55,
    }}>{children}</p>
  );
};

/* ================================================================ page == */
export default function FamilySettingsPage({ orgCode, orgName }: Props) {
  const { currentUser } = useAuth();

  /* ── the household's own documents ─────────────────────────────────── */
  const [orgData, setOrgData] = useState<Record<string, unknown> | null>(null);
  const [busId, setBusId] = useState('');
  const [driverUid, setDriverUid] = useState('');

  useEffect(() => {
    if (!orgCode) return;
    const unsub = onSnapshot(doc(db, 'organizations', orgCode), (snap) => {
      setOrgData((snap.data() as Record<string, unknown>) || null);
    }, () => setOrgData(null));
    return () => unsub();
  }, [orgCode]);

  useEffect(() => {
    if (!orgCode) return;
    const unsub = onSnapshot(doc(db, 'familySchedules', orgCode), (snap) => {
      const d = snap.data() || {};
      setBusId(String(d.busId || ''));
      setDriverUid(String(d.driverUid || ''));
    }, () => { /* the schedule may not exist yet on a half-set-up household */ });
    return () => unsub();
  }, [orgCode]);

  const familySettings = useMemo(
    () => (orgData?.familySettings || {}) as Record<string, unknown>,
    [orgData],
  );
  const driverMode = String(familySettings.driverMode || '');
  const plate = String(familySettings.vehiclePlate || busId || '');
  const parentDrives = driverMode === 'parent_drives';

  /* ── the driver ────────────────────────────────────────────────────── */
  const [driver, setDriver] = useState<DriverInfo | null>(null);
  const [driverEmail, setDriverEmail] = useState('');
  const [driverName, setDriverName] = useState('');
  const [driverBusy, setDriverBusy] = useState(false);
  const [driverErr, setDriverErr] = useState('');
  const [driverOk, setDriverOk] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!driverUid) { setDriver(null); return; }
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', driverUid));
        if (!alive) return;
        const d = snap.data() || {};
        setDriver({
          uid: driverUid,
          name: String(d.displayName || d.name || 'Your driver'),
          email: String(d.email || ''),
          addedAt: toDate(d.createdAt),
        });
      } catch {
        if (alive) setDriver({ uid: driverUid, name: 'Your driver', email: '', addedAt: null });
      }
    })();
    return () => { alive = false; };
  }, [driverUid]);

  const doInviteDriver = useCallback(async (replace: boolean) => {
    setDriverErr(''); setDriverOk(''); setTempPassword('');
    if (!EMAIL_RE.test(driverEmail.trim())) {
      setDriverErr("That does not look like an email address. We need your driver's email to make their account.");
      return;
    }
    setDriverBusy(true);
    const res = await inviteFamilyDriver({
      orgCode, email: driverEmail, displayName: driverName, replace,
    });
    setDriverBusy(false);
    setConfirmReplace(false);
    if (!res.ok) {
      // 409 is the "already has a driver" refusal — turn it into the confirm
      // step rather than an error the parent cannot act on.
      if (res.status === 409) { setConfirmReplace(true); setDriverErr(res.message || ''); return; }
      setDriverErr(res.message || 'We could not invite your driver.');
      return;
    }
    setDriverEmail(''); setDriverName('');
    if (res.password) {
      setTempPassword(res.password);
      setDriverOk('');
    } else {
      setDriverOk('Added. They already have a Trasse account — they sign in with the password they use today.');
    }
  }, [orgCode, driverEmail, driverName]);

  const doRemoveDriver = useCallback(async () => {
    if (!driver) return;
    setDriverErr(''); setDriverOk(''); setDriverBusy(true);
    const res = await removeFamilyDriver(orgCode, driver.uid);
    setDriverBusy(false);
    setConfirmRemove(false);
    if (!res.ok) { setDriverErr(res.message || 'We could not remove your driver.'); return; }
    setDriverOk('Removed. Their phone has stopped tracking, and the runs they drove stay in your history.');
  }, [orgCode, driver]);

  /* ── the second adult ──────────────────────────────────────────────── */
  const [coParents, setCoParents] = useState<CoParent[]>([]);
  const [pending, setPending] = useState<PendingInvite[]>([]);
  const [coEmail, setCoEmail] = useState('');
  const [coBusy, setCoBusy] = useState(false);
  const [coErr, setCoErr] = useState('');
  const [coOk, setCoOk] = useState('');

  const loadHousehold = useCallback(async () => {
    if (!orgCode) return;
    try {
      const snap = await getDocs(query(collection(db, 'users'), where('orgCode', '==', orgCode)));
      const rows: CoParent[] = [];
      snap.forEach((s) => {
        const d = s.data() as Record<string, unknown>;
        const role = String(d.role || '');
        // The owner is a fleet_admin and the driver is a bus_driver; the second
        // adult is the only 'parent'/'client' in a household.
        if (role === 'parent' || role === 'client') {
          rows.push({
            uid: s.id,
            name: String(d.displayName || d.name || 'Co-parent'),
            email: String(d.email || ''),
          });
        }
      });
      setCoParents(rows);
    } catch { /* non-fatal: the section simply shows nobody */ }
    try {
      const snap = await getDocs(query(collection(db, 'fleetInvites'), where('orgCode', '==', orgCode)));
      const rows: PendingInvite[] = [];
      snap.forEach((s) => {
        const d = s.data() as Record<string, unknown>;
        if (d.used !== true && String(d.role || '') === 'parent') {
          rows.push({ id: s.id, email: String(d.email || '') });
        }
      });
      setPending(rows);
    } catch { /* non-fatal */ }
  }, [orgCode]);

  useEffect(() => { loadHousehold(); }, [loadHousehold]);

  const doInviteCoParent = useCallback(async () => {
    setCoErr(''); setCoOk('');
    const email = coEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { setCoErr('That does not look like an email address.'); return; }
    if (email === String(currentUser?.email || '').toLowerCase()) {
      setCoErr('That is your own address — you are already in this household.');
      return;
    }
    setCoBusy(true);
    try {
      const uid = currentUser?.uid || '';
      let familyHousehold: {
        primaryParentId: string;
        busId?: string;
        children?: unknown[];
        childAvatars?: unknown[];
        linkedChildren?: unknown[];
        busChildren?: unknown;
      } | undefined;
      if (uid) {
        const snap = await getDoc(doc(db, 'users', uid));
        const d = snap.data() || {};
        familyHousehold = {
          primaryParentId: uid,
          busId: typeof d.busId === 'string' ? d.busId : undefined,
          children: Array.isArray(d.children) ? d.children : [],
          childAvatars: Array.isArray(d.childAvatars) ? d.childAvatars : [],
          linkedChildren: Array.isArray(d.linkedChildren) ? d.linkedChildren : [],
          busChildren: d.busChildren,
        };
      }
      const res = await sendFleetInvite(
        email, orgCode, orgName || orgCode, 'parent', uid,
        familyHousehold ? { familyHousehold } : undefined,
      );
      if (!res.ok) { setCoErr(res.error || 'We could not send that invite.'); return; }
      setCoEmail('');
      setCoOk(`Invite sent to ${email}. They set their own password from the link, and see the same runs as you.`);
      loadHousehold();
    } finally {
      setCoBusy(false);
    }
  }, [coEmail, orgCode, orgName, currentUser, loadHousehold]);

  /* ── the invite code ───────────────────────────────────────────────── */
  const [refCode, setRefCode] = useState('');
  const [refPayEnabled, setRefPayEnabled] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!orgCode) return;
    (async () => {
      const res = await fetchMyFamilyReferralCode(orgCode);
      if (!alive || !res.ok || !res.code) return;
      setRefCode(res.code);
      setRefPayEnabled(res.payEnabled === true);
    })();
    return () => { alive = false; };
  }, [orgCode]);

  const shareLink = refCode && typeof window !== 'undefined'
    ? `${window.location.origin}/families/start?ref=${refCode}`
    : '';

  const copyShare = useCallback(async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      /* Clipboard is permission-gated in some browsers; the link is on screen
         and selectable either way, so this is not worth an error message. */
    }
  }, [shareLink]);

  /* ── the pins ──────────────────────────────────────────────────────── */
  const [home, setHome] = useState<LatLng | null>(null);
  const [school, setSchool] = useState<LatLng | null>(null);
  const [pinsLoaded, setPinsLoaded] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);
  const [pinErr, setPinErr] = useState('');
  const [pinOk, setPinOk] = useState('');
  const [editingPins, setEditingPins] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!orgData || pinsLoaded) return;
    (async () => {
      try {
        await ensureLocationDek();
        const h = await openLatLng(orgData.homeLocations);
        const s = await openLatLng(orgData.schoolLocations);
        if (!alive) return;
        setHome(h); setSchool(s); setPinsLoaded(true);
      } catch {
        if (alive) setPinsLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [orgData, pinsLoaded]);

  const savePins = useCallback(async () => {
    setPinErr(''); setPinOk('');
    if (!home) { setPinErr('Drop a pin on your home first.'); return; }
    setPinBusy(true);
    try {
      const sealedHome = await sealLatLng(home.lat, home.lng);
      const sealedSchool = school ? await sealLatLng(school.lat, school.lng) : null;
      if (!sealedHome) throw new Error('seal failed');

      await setDoc(doc(db, 'organizations', orgCode), {
        homeLocations: sealedHome,
        schoolLocations: sealedSchool,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      /* The child stops are the half that actually ends the evening run.
         Read-modify-write, because each child carries its own sealed stop. */
      if (busId) {
        const busSnap = await getDoc(doc(db, 'buses', busId));
        const kids = busSnap.data()?.children;
        if (Array.isArray(kids) && kids.length) {
          const next = kids.map((k: Record<string, unknown>) => ({ ...k, stopLocation: sealedHome }));
          await setDoc(doc(db, 'buses', busId), {
            children: next,
            updatedAt: serverTimestamp(),
          }, { merge: true });
        }
      }
      setPinOk('Saved. The run now starts and ends at your new pin.');
      setEditingPins(false);
    } catch (e) {
      console.error('[families] pin save failed', e);
      const code = typeof e === 'object' && e && 'code' in e ? String((e as { code?: string }).code) : '';
      setPinErr(code === 'permission-denied'
        ? 'Could not save your pins. Sign out and back in, then try again. If it still fails, household saving may need a server update.'
        : 'We could not save your pins. Check your connection and try again.');
    } finally {
      setPinBusy(false);
    }
  }, [home, school, orgCode, busId]);

  /* ── the car's nickname (the plate is not editable — see the header) ── */
  const [nickname, setNickname] = useState('');
  const [nickLoaded, setNickLoaded] = useState(false);
  const [nickBusy, setNickBusy] = useState(false);
  const [nickOk, setNickOk] = useState('');
  const [nickErr, setNickErr] = useState('');

  useEffect(() => {
    let alive = true;
    if (!busId || nickLoaded) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'buses', busId));
        if (!alive) return;
        setNickname(String(snap.data()?.nickname || ''));
        setNickLoaded(true);
      } catch { if (alive) setNickLoaded(true); }
    })();
    return () => { alive = false; };
  }, [busId, nickLoaded]);

  const saveNickname = useCallback(async () => {
    setNickErr(''); setNickOk(''); setNickBusy(true);
    try {
      await setDoc(doc(db, 'buses', busId), {
        nickname: nickname.trim(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setNickOk('Saved.');
    } catch (e) {
      console.error('[families] nickname save failed', e);
      const code = typeof e === 'object' && e && 'code' in e ? String((e as { code?: string }).code) : '';
      setNickErr(code === 'permission-denied'
        ? 'Could not save that. Sign out and back in, then try again.'
        : 'We could not save that. Try again.');
    } finally { setNickBusy(false); }
  }, [busId, nickname]);

  /* ============================================================ render == */
  return (
    <div style={FAMILY_PAGE_WRAP}>

      {/* ── driver ──────────────────────────────────────────────────── */}
      <Panel>
        <H>Your driver</H>
        <Sub>
          A household has one person who drives. Replacing them is a real event, so Trasse treats it
          as one — the new driver gets their own account, and the old one stops tracking immediately.
        </Sub>

        {parentDrives && !driver && (
          <div style={{
            marginTop: 18, padding: '14px 16px', borderRadius: 14,
            background: FAM.tint, border: `1px solid ${FAM.tint2}`,
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: FAM.ink }}>You drive the school run</div>
            <div style={{ marginTop: 4, fontSize: 13.5, color: FAM.ink2, lineHeight: 1.55 }}>
              Your own phone records the run. Invite someone below if that changes — you will keep
              access to everything either of you drove.
            </div>
          </div>
        )}

        {driver && (
          <div style={{
            marginTop: 18, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
            padding: '14px 16px', borderRadius: 14, background: FAM.tint, border: `1px solid ${FAM.tint2}`,
          }}>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div style={{ fontSize: 15.5, fontWeight: 700, color: FAM.ink }}>{driver.name}</div>
              <div style={{ fontSize: 13.5, color: FAM.ink2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {driver.email}
              </div>
              {driver.addedAt && (
                <div style={{ fontSize: 12.5, color: FAM.muted, marginTop: 3 }}>
                  Added {fmtDay(driver.addedAt)}
                </div>
              )}
            </div>
            <button type="button" style={quietBtn} disabled={driverBusy}
              onClick={() => { setConfirmRemove(true); setDriverErr(''); setDriverOk(''); }}>
              Remove
            </button>
          </div>
        )}

        {confirmRemove && driver && (
          <div style={{
            marginTop: 12, padding: '14px 16px', borderRadius: 14,
            border: `1px solid ${FAM.dangerTint}`, background: FAM.dangerTint,
          }}>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: FAM.danger }}>
              Remove {driver.name} from your household?
            </div>
            <p style={{ margin: '6px 0 12px', fontSize: 13.5, color: FAM.ink2, lineHeight: 1.55 }}>
              Their phone stops tracking your car straight away and they lose access to your
              household. The runs they already drove stay in your history — nothing is deleted.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" onClick={doRemoveDriver} disabled={driverBusy}
                style={{ ...quietBtn, borderColor: FAM.danger, color: FAM.danger, fontWeight: 700 }}>
                {driverBusy ? 'Removing…' : 'Yes, remove them'}
              </button>
              <button type="button" style={quietBtn} onClick={() => setConfirmRemove(false)}>Keep them</button>
            </div>
          </div>
        )}

        {/* invite / replace */}
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: FAM.ink2, marginBottom: 10 }}>
            {driver ? 'Replace your driver' : 'Invite your driver'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 10 }}>
            <Field label="Their name">
              <input style={inputStyle} value={driverName} placeholder="Peter Kimani"
                onChange={(e) => { setDriverName(e.target.value); setDriverErr(''); }} />
            </Field>
            <Field label="Their email">
              <input style={inputStyle} value={driverEmail} placeholder="peter@example.com"
                autoComplete="off" inputMode="email"
                onChange={(e) => { setDriverEmail(e.target.value); setDriverErr(''); }} />
            </Field>
          </div>

          {confirmReplace ? (
            <div style={{ marginTop: 12 }}>
              <Note tone="warn">
                Your car already has a driver. Replacing them removes {driver?.name || 'the current driver'} and
                puts {driverEmail.trim() || 'the new driver'} on the car instead.
              </Note>
              <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                <button type="button" style={primaryBtn(driverBusy)} disabled={driverBusy}
                  onClick={() => doInviteDriver(true)}>
                  {driverBusy ? 'Replacing…' : 'Yes, replace the driver'}
                </button>
                <button type="button" style={quietBtn}
                  onClick={() => { setConfirmReplace(false); setDriverErr(''); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <button type="button" style={{ ...primaryBtn(driverBusy), marginTop: 12 }} disabled={driverBusy}
              onClick={() => doInviteDriver(false)}>
              {driverBusy ? 'Sending…' : driver ? 'Invite replacement' : 'Invite driver'}
            </button>
          )}

          {tempPassword && (
            <div style={{
              marginTop: 12, padding: '14px 16px', borderRadius: 12,
              background: FAM.posTint, border: `1px solid ${FAM.pos2}33`,
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: FAM.pos }}>
                Their account is ready — give them this password
              </div>
              <div style={{
                margin: '10px 0', padding: '10px 12px', borderRadius: 9, background: '#fff',
                fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize: 17,
                fontWeight: 700, letterSpacing: '.04em', color: FAM.ink, wordBreak: 'break-all',
              }}>{tempPassword}</div>
              <div style={{ fontSize: 12.5, color: FAM.ink2, lineHeight: 1.55 }}>
                ⚠ Shown once. Trasse cannot show it again — if it is lost, they use
                "Forgot password" on the app's sign-in screen. They log in on the Trasse app
                with their email and this password.
              </div>
            </div>
          )}

          {driverErr && !confirmReplace && <Note tone="bad">{driverErr}</Note>}
          {driverOk && <Note tone="ok">{driverOk}</Note>}
        </div>
      </Panel>

      {/* ── co-parent ───────────────────────────────────────────────── */}
      <Panel>
        <H>The second adult</H>
        <Sub>
          The other parent sees the same runs, the same hours and the same car — everything except
          billing and this page. They sign in with their own email and their own password.
        </Sub>

        {coParents.length > 0 && (
          <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {coParents.map((c) => (
              <div key={c.uid} style={{
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                padding: '11px 14px', borderRadius: 12, background: FAM.tint, border: `1px solid ${FAM.tint2}`,
              }}>
                <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: FAM.ink }}>{c.name}</div>
                  <div style={{ fontSize: 13, color: FAM.ink2 }}>{c.email}</div>
                </div>
                <span style={{ fontSize: 11.5, fontWeight: 800, color: FAM.pos, letterSpacing: '.04em' }}>
                  IN YOUR HOUSEHOLD
                </span>
              </div>
            ))}
          </div>
        )}

        {pending.length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pending.map((p) => (
              <div key={p.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                padding: '11px 14px', borderRadius: 12, border: `1px dashed ${FAM.tint2}`, background: '#FAF9F6',
              }}>
                <div style={{ flex: '1 1 200px', fontSize: 14, color: FAM.ink2, minWidth: 0 }}>{p.email}</div>
                <span style={{ fontSize: 11.5, fontWeight: 800, color: FAM.amber, letterSpacing: '.04em' }}>
                  INVITE SENT
                </span>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 18, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 240px', minWidth: 0 }}>
            <Field label="Their email">
              <input style={inputStyle} value={coEmail} placeholder="grace@example.com"
                autoComplete="off" inputMode="email"
                onChange={(e) => { setCoEmail(e.target.value); setCoErr(''); }} />
            </Field>
          </div>
          <button type="button" style={primaryBtn(coBusy)} disabled={coBusy} onClick={doInviteCoParent}>
            {coBusy ? 'Sending…' : 'Send invite'}
          </button>
        </div>

        {coErr && <Note tone="bad">{coErr}</Note>}
        {coOk && <Note tone="ok">{coOk}</Note>}
      </Panel>

      {/* ── invite another family ───────────────────────────────────── */}
      {refCode && (
        <Panel>
          <H>Invite another family</H>
          <Sub>
            Your own code. A household that signs up with it is recorded as your introduction —
            permanently, and from the moment they start.
          </Sub>

          <div style={{
            marginTop: 18, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
            padding: '16px 18px', borderRadius: 14, background: FAM.tint, border: `1px solid ${FAM.tint2}`,
          }}>
            <div style={{
              fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize: 26, fontWeight: 800,
              letterSpacing: '.14em', color: FAM.ink,
            }}>{refCode}</div>
            <button type="button" onClick={copyShare} style={{ ...quietBtn, marginLeft: 'auto' }}>
              {copied ? 'Link copied' : 'Copy invite link'}
            </button>
          </div>

          {shareLink && (
            <div style={{
              marginTop: 10, padding: '10px 13px', borderRadius: 10, background: '#FAF9F6',
              border: `1px solid ${FAM.line}`, fontSize: 12.5, color: FAM.ink2, wordBreak: 'break-all',
            }}>{shareLink}</div>
          )}

          <p style={{ margin: '14px 0 0', fontSize: 13.5, lineHeight: 1.6, color: FAM.ink2, maxWidth: 560 }}>
            {refPayEnabled
              ? 'When a household you introduced starts paying, you get 10% off your next two months.'
              : 'Your code works and every household you introduce is recorded. What that earns you is still being settled, so we are not going to promise you a number we might change.'}
          </p>
        </Panel>
      )}

      {/* ── the school ──────────────────────────────────────────────── */}
      <FamilySchoolLinkPanel
        orgCode={orgCode}
        schoolName={String(familySettings.schoolName || '')}
      />

      {/* ── pins ────────────────────────────────────────────────────── */}
      <Panel>
        <H>Home and school</H>
        <Sub>
          Where the run starts and where it ends. Your home pin is stored sealed — Trasse can use it
          to end a run, but a household's address is never left readable the way a school's is.
        </Sub>

        {!editingPins && (
          <div style={{ marginTop: 18 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12 }}>
              {[
                { k: 'Home', v: home ? `${home.lat.toFixed(5)}, ${home.lng.toFixed(5)}` : 'No pin yet' },
                { k: 'School', v: school ? `${school.lat.toFixed(5)}, ${school.lng.toFixed(5)}` : 'Not set' },
              ].map((row) => (
                <div key={row.k} style={{
                  padding: '12px 14px', borderRadius: 12, background: '#FAF9F6', border: `1px solid ${FAM.line}`,
                }}>
                  <div style={labelStyle}>{row.k}</div>
                  <div style={{
                    marginTop: 4, fontSize: 14, fontWeight: 600, color: FAM.ink,
                    fontVariantNumeric: 'tabular-nums',
                  }}>{row.v}</div>
                </div>
              ))}
            </div>
            <button type="button" style={{ ...quietBtn, marginTop: 14 }}
              onClick={() => { setEditingPins(true); setPinOk(''); setPinErr(''); }}>
              Change the pins
            </button>
            {pinOk && <Note tone="ok">{pinOk}</Note>}
          </div>
        )}

        {editingPins && (
          <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div>
              <div style={{ ...labelStyle, marginBottom: 6 }}>Home — drop the pin on your gate, not the road</div>
              <LocationPicker value={home} onChange={setHome} heightPx={240} />
            </div>
            <div>
              <div style={{ ...labelStyle, marginBottom: 6 }}>School — optional</div>
              <LocationPicker value={school} onChange={setSchool} heightPx={240} />
            </div>

            <Note tone="warn">
              Moving the home pin also moves where your child's run finishes. Runs already recorded
              keep the pin they happened at.
            </Note>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" style={primaryBtn(pinBusy)} disabled={pinBusy} onClick={savePins}>
                {pinBusy ? 'Saving…' : 'Save pins'}
              </button>
              <button type="button" style={quietBtn} disabled={pinBusy}
                onClick={() => { setEditingPins(false); setPinsLoaded(false); setPinErr(''); }}>
                Cancel
              </button>
            </div>
            {pinErr && <Note tone="bad">{pinErr}</Note>}
          </div>
        )}
      </Panel>

      {/* ── the car ─────────────────────────────────────────────────── */}
      <Panel>
        <H>Your car</H>
        <Sub>
          One household, one car. Its number plate is how every run, route and refuel is filed, so it
          is fixed once the household is created.
        </Sub>

        <div style={{
          marginTop: 18, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12,
        }}>
          <div style={{ padding: '12px 14px', borderRadius: 12, background: '#FAF9F6', border: `1px solid ${FAM.line}` }}>
            <div style={labelStyle}>Number plate</div>
            <div style={{
              marginTop: 4, fontSize: 16, fontWeight: 800, letterSpacing: '.06em', color: FAM.ink,
            }}>{plate || '—'}</div>
          </div>
          <div>
            <Field label="What you call it">
              <input style={inputStyle} value={nickname} placeholder="The blue Toyota"
                onChange={(e) => { setNickname(e.target.value); setNickOk(''); setNickErr(''); }} />
            </Field>
            <button type="button" style={{ ...quietBtn, marginTop: 10 }} disabled={nickBusy || !busId}
              onClick={saveNickname}>
              {nickBusy ? 'Saving…' : 'Save name'}
            </button>
          </div>
        </div>

        <p style={{ margin: '14px 0 0', fontSize: 13, color: FAM.muted, lineHeight: 1.6 }}>
          Changed cars? The plate is the car's identity here, so a new car is a change we make with
          you rather than a field you overwrite — every trip, route and refuel is filed under the old
          plate and would be orphaned. Email us and we will move your household across.
        </p>

        {nickOk && <Note tone="ok">{nickOk}</Note>}
        {nickErr && <Note tone="bad">{nickErr}</Note>}
      </Panel>

      {/* ── the household ───────────────────────────────────────────── */}
      <Panel>
        <H>Your household</H>
        <Sub>Who this account belongs to.</Sub>
        <div style={{
          marginTop: 18, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 12,
        }}>
          {[
            { k: 'Household', v: String(orgData?.name || orgName || orgCode) },
            { k: 'Code', v: orgCode },
            { k: 'Account email', v: String(orgData?.adminEmail || currentUser?.email || '—') },
            { k: 'School', v: String(familySettings.schoolName || 'Not set') },
          ].map((row) => (
            <div key={row.k} style={{
              padding: '12px 14px', borderRadius: 12, background: '#FAF9F6',
              border: `1px solid ${FAM.line}`, minWidth: 0,
            }}>
              <div style={labelStyle}>{row.k}</div>
              <div style={{
                marginTop: 4, fontSize: 14, fontWeight: 600, color: FAM.ink,
                overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{row.v}</div>
            </div>
          ))}
        </div>
        <p style={{ margin: '14px 0 0', fontSize: 13, color: FAM.muted, lineHeight: 1.6 }}>
          Your account email is how you sign in, so changing it is a support job rather than a form —
          it has to move your login at the same time, and we would rather not lock you out of your
          own household. Email us and we will do it with you.
        </p>
      </Panel>
    </div>
  );
}
